/**
 * Cross-tab coordination for POST /api/v1/auth/refresh.
 * Refresh tokens rotate server-side; parallel refresh calls from multiple tabs can 401.
 * One tab acquires a short-lived localStorage lock; others wait for `token` to update.
 */

const LOCK_KEY = 'insightia_refresh_coord_lock';
const LOCK_TTL_MS = 25000;

/**
 * @returns {boolean} True if this tab holds the lock (or SSR / no window).
 */
export function tryAcquireCrossTabRefreshLock() {
  if (typeof window === 'undefined') return true;

  const now = Date.now();
  const raw = window.localStorage.getItem(LOCK_KEY);
  if (raw) {
    try {
      const { until } = JSON.parse(raw);
      if (typeof until === 'number' && until > now) {
        return false;
      }
    } catch {
      // Malformed — overwrite below
    }
  }

  const owner = `${now}-${Math.random().toString(36).slice(2, 11)}`;
  const until = now + LOCK_TTL_MS;
  const payload = JSON.stringify({ owner, until });
  window.localStorage.setItem(LOCK_KEY, payload);

  const verify = window.localStorage.getItem(LOCK_KEY);
  if (verify !== payload) return false;

  try {
    const parsed = JSON.parse(verify);
    return parsed.owner === owner;
  } catch {
    return false;
  }
}

export function releaseCrossTabRefreshLock() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(LOCK_KEY);
  } catch {
    // Private mode / quota
  }
}

/**
 * Resolves true when another tab updates `localStorage.token` to a new value.
 *
 * @param {number} maxMs
 * @returns {Promise<boolean>}
 */
export function waitForTokenRefreshFromOtherTab(maxMs = LOCK_TTL_MS) {
  if (typeof window === 'undefined') return Promise.resolve(false);

  const startToken = window.localStorage.getItem('token');
  const deadline = Date.now() + maxMs;

  return new Promise((resolve) => {
    let done = false;

    const finish = (ok) => {
      if (done) return;
      done = true;
      cleanup();
      resolve(ok);
    };

    const check = () => {
      const t = window.localStorage.getItem('token');
      if (t && t !== startToken) {
        finish(true);
        return;
      }
      if (Date.now() > deadline) {
        finish(false);
      }
    };

    const onStorage = (e) => {
      if (e.key === 'token' && e.newValue && e.newValue !== startToken) {
        finish(true);
      }
    };

    window.addEventListener('storage', onStorage);
    const interval = window.setInterval(check, 80);

    function cleanup() {
      window.removeEventListener('storage', onStorage);
      window.clearInterval(interval);
    }
  });
}
